import { useUIColors } from "@/core/hooks";
import React, { useEffect, useRef, useState } from "react";
import {
  LayoutChangeEvent,
  NativeScrollEvent,
  NativeSyntheticEvent,
  ScrollView,
  StyleSheet,
  View,
} from "react-native";

const AUTO_SCROLL_INTERVAL_MS = 3000;

export interface CardCarouselProps {
  children: React.ReactNode;
  /** Suspends auto-scroll while true (e.g. a card's 3-dot menu popup or
   * detail sheet is open) — resumes once it goes back to false. Relying on
   * touch events alone doesn't cover this: the tap that opens a popup ends
   * well before the popup itself closes, so auto-scroll would resume while
   * it's still on screen. */
  paused?: boolean;
}

function getCarouselItemKey(item: React.ReactNode, prefix: string): string {
  if (React.isValidElement(item) && item.key != null) {
    return `${prefix}-${String(item.key)}`;
  }
  return prefix;
}

/**
 * Horizontal, paged wrapper for status-style cards (e.g. Upcoming Appointment,
 * Reserved Unit). Renders a single card directly with no dots; with 2+ cards
 * it becomes swipeable with pagination dots below and auto-advances on a timer.
 */
export function CardCarousel({ children, paused = false }: Readonly<CardCarouselProps>) {
  const colors = useUIColors();
  const items = React.Children.toArray(children);
  const [activeIndex, setActiveIndex] = useState(0);
  const [cardWidth, setCardWidth] = useState(0);
  const scrollRef = useRef<ScrollView>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startAutoScroll = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    if (paused || items.length < 2 || !cardWidth) return;
    timerRef.current = setInterval(() => {
      setActiveIndex((prev) => {
        const next = (prev + 1) % items.length;
        scrollRef.current?.scrollTo({ x: next * cardWidth, animated: true });
        return next;
      });
    }, AUTO_SCROLL_INTERVAL_MS);
  };

  useEffect(() => {
    startAutoScroll();
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length, cardWidth, paused]);

  if (items.length === 0) {
    return null;
  }

  if (items.length === 1) {
    return <>{items[0]}</>;
  }

  const handleLayout = (event: LayoutChangeEvent) => {
    setCardWidth(event.nativeEvent.layout.width);
  };

  const handleMomentumScrollEnd = (
    event: NativeSyntheticEvent<NativeScrollEvent>,
  ) => {
    if (!cardWidth) return;
    const offsetX = event.nativeEvent.contentOffset.x;
    const index = Math.round(Math.abs(offsetX) / cardWidth);
    setActiveIndex(Math.min(index, items.length - 1));
    startAutoScroll();
  };

  return (
    <View onLayout={handleLayout}>
      {cardWidth > 0 ? (
        <ScrollView
          ref={scrollRef}
          horizontal
          pagingEnabled
          showsHorizontalScrollIndicator={false}
          onMomentumScrollEnd={handleMomentumScrollEnd}
          onScrollBeginDrag={() => {
            if (timerRef.current) {
              clearInterval(timerRef.current);
            }
          }}
        >
          {items.map((item) => (
            <View
              key={getCarouselItemKey(item, "card")}
              style={{ width: cardWidth }}
            >
              {item}
            </View>
          ))}
        </ScrollView>
      ) : null}
      <View style={styles.dotsRow}>
        {items.map((item, index) => (
          <View
            key={getCarouselItemKey(item, "dot")}
            style={[
              styles.dot,
              {
                backgroundColor:
                  index === activeIndex
                    ? colors.buttonDefault
                    : colors.outlineInactive,
              },
            ]}
          />
        ))}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  dotsRow: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    gap: 6,
    marginTop: 10,
  },
  dot: {
    width: 6,
    height: 6,
    borderRadius: 3,
  },
});

export default CardCarousel;
